import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  FileText, Download, RefreshCw, Calendar, Printer,
  BookOpen, BarChart3, TrendingUp, TrendingDown, DollarSign,
  Search, ChevronsUpDown, Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "../../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger
} from "../../../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from "../../../components/ui/command";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

// Combobox reutilizable para cuentas
const CuentaCombobox = ({ cuentas, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const selected = cuentas.find(c => c.id.toString() === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between text-left font-normal h-9 px-3">
          <span className="truncate">{selected ? `${selected.codigo} - ${selected.nombre || selected.descripcion}` : (placeholder || "Seleccionar cuenta...")}</span>
          <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar por codigo o nombre..." />
          <CommandList>
            <CommandEmpty>No se encontraron cuentas.</CommandEmpty>
            <CommandGroup className="max-h-[200px] overflow-y-auto">
              {cuentas.map(c => (
                <CommandItem key={c.id} value={`${c.codigo} ${c.nombre || c.descripcion}`}
                  onSelect={() => { onChange(c.id.toString()); setOpen(false); }}>
                  <Check className={"mr-2 h-4 w-4 " + (value === c.id.toString() ? "opacity-100" : "opacity-0")} />
                  <span className="font-mono text-xs mr-2">{c.codigo}</span>
                  <span className="truncate">{c.nombre || c.descripcion}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Filtro de periodo compartido
const FiltrosPeriodo = ({ fechaDesde, setFechaDesde, fechaHasta, setFechaHasta, onConsultar, loading, children }) => (
  <div className="flex flex-wrap gap-4 items-end">
    <div>
      <Label>Desde</Label>
      <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="w-[160px]" />
    </div>
    <div>
      <Label>Hasta</Label>
      <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="w-[160px]" />
    </div>
    {children}
    <Button onClick={onConsultar} disabled={loading} className="bg-[#2853B9] hover:bg-[#1E3876]">
      {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
      Consultar
    </Button>
  </div>
);

const ReportesContables = () => {
  const [activeTab, setActiveTab] = useState("mayor");
  const [loading, setLoading] = useState(false);
  const [ejercicios, setEjercicios] = useState([]);
  const [ejercicioId, setEjercicioId] = useState("");
  const [cuentas, setCuentas] = useState([]);
  
  // Mayor: seleccion multiple de cuentas
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState("");
  const [cuentasSeleccionadas, setCuentasSeleccionadas] = useState([]);
  const [modoMayor, setModoMayor] = useState("individual"); // "individual" | "multiple"
  
  // Datos de reportes
  const [libroDiario, setLibroDiario] = useState([]);
  const [libroMayor, setLibroMayor] = useState(null);
  const [mayoresMultiples, setMayoresMultiples] = useState([]);
  const [balanceSumas, setBalanceSumas] = useState([]);
  const [balanceGeneral, setBalanceGeneral] = useState(null);
  const [estadoResultados, setEstadoResultados] = useState(null);

  // Filtros
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  useEffect(() => {
    fetchEjercicios();
    fetchCuentas();
  }, []);

  const fetchEjercicios = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios`);
      setEjercicios(response.data);
      const ejercicioAbierto = response.data.find(e => !e.cerrado);
      if (ejercicioAbierto) setEjercicioId(ejercicioAbierto.id.toString());
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchCuentas = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/plan-cuentas`);
      const imputables = response.data.filter(c => c.es_imputable);
      setCuentas(imputables);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    if (fechaDesde) params.append("fecha_desde", fechaDesde);
    if (fechaHasta) params.append("fecha_hasta", fechaHasta);
    return params;
  };

  const fetchLibroDiario = async () => {
    if (!ejercicioId) { toast.error("Seleccione un ejercicio"); return; }
    setLoading(true);
    try {
      const params = buildParams();
      const response = await axios.get(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicioId}/libro-diario?${params}`
      );
      setLibroDiario(response.data);
    } catch (error) {
      toast.error("Error al cargar libro diario");
    } finally { setLoading(false); }
  };

  const fetchLibroMayor = async () => {
    if (!ejercicioId || !cuentaSeleccionada) { toast.error("Seleccione ejercicio y cuenta"); return; }
    setLoading(true);
    try {
      const params = buildParams();
      const response = await axios.get(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicioId}/mayor/${cuentaSeleccionada}?${params}`
      );
      setLibroMayor(response.data);
      setMayoresMultiples([]);
    } catch (error) {
      toast.error("Error al cargar libro mayor");
    } finally { setLoading(false); }
  };

  const fetchMayoresMultiples = async () => {
    if (!ejercicioId || cuentasSeleccionadas.length === 0) {
      toast.error("Seleccione ejercicio y al menos una cuenta");
      return;
    }
    setLoading(true);
    try {
      const params = buildParams();
      const resultados = [];
      for (const cuentaId of cuentasSeleccionadas) {
        const response = await axios.get(
          `${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicioId}/mayor/${cuentaId}?${params}`
        );
        resultados.push(response.data);
      }
      setMayoresMultiples(resultados);
      setLibroMayor(null);
    } catch (error) {
      toast.error("Error al cargar mayores");
    } finally { setLoading(false); }
  };

  const fetchBalanceSumas = async () => {
    if (!ejercicioId) { toast.error("Seleccione un ejercicio"); return; }
    setLoading(true);
    try {
      const params = buildParams();
      const response = await axios.get(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicioId}/sumas-saldos?${params}`
      );
      setBalanceSumas(response.data.lineas || []);
    } catch (error) {
      toast.error("Error al cargar balance");
    } finally { setLoading(false); }
  };

  const fetchBalanceGeneral = async () => {
    if (!ejercicioId) { toast.error("Seleccione un ejercicio"); return; }
    setLoading(true);
    try {
      const params = buildParams();
      const response = await axios.get(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicioId}/balance-general?${params}`
      );
      setBalanceGeneral(response.data);
    } catch (error) {
      toast.error("Error al cargar balance general");
    } finally { setLoading(false); }
  };

  const fetchEstadoResultados = async () => {
    if (!ejercicioId) { toast.error("Seleccione un ejercicio"); return; }
    setLoading(true);
    try {
      const params = buildParams();
      const response = await axios.get(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicioId}/estado-resultados?${params}`
      );
      setEstadoResultados(response.data);
    } catch (error) {
      toast.error("Error al cargar estado de resultados");
    } finally { setLoading(false); }
  };

  const toggleCuentaMultiple = (cuentaId) => {
    setCuentasSeleccionadas(prev =>
      prev.includes(cuentaId) ? prev.filter(id => id !== cuentaId) : [...prev, cuentaId]
    );
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('es-AR');
  };

  const renderMayorTable = (mayorData) => {
    const tieneMonedaExtranjera = mayorData.movimientos?.some(m => m.moneda_id && m.moneda_id !== 1);
    const colSpan = tieneMonedaExtranjera ? 10 : 6;
    return (
      <div className="space-y-4 mb-8" key={mayorData.cuenta_id || mayorData.cuenta?.id}>
        <div className="p-4 bg-gray-50 rounded-lg flex items-center gap-3">
          <div>
            <h4 className="font-semibold">{mayorData.cuenta_codigo || mayorData.cuenta?.codigo} - {mayorData.cuenta_descripcion || mayorData.cuenta?.nombre}</h4>
            <p className="text-sm text-gray-600">Saldo inicial: {formatCurrency(mayorData.saldo_inicial || 0)}</p>
          </div>
          {tieneMonedaExtranjera && <Badge className="bg-blue-100 text-blue-700"><DollarSign className="w-3 h-3 mr-1" />Bimonetario</Badge>}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Asiento</TableHead>
                <TableHead>Concepto</TableHead>
                {tieneMonedaExtranjera && <>
                  <TableHead className="w-16">Mon.</TableHead>
                  <TableHead className="text-right">Orig. Debe</TableHead>
                  <TableHead className="text-right">Orig. Haber</TableHead>
                  <TableHead className="text-right w-16">TC</TableHead>
                </>}
                <TableHead className={"text-right" + (tieneMonedaExtranjera ? " bg-blue-50" : "")}>Debe ARS</TableHead>
                <TableHead className={"text-right" + (tieneMonedaExtranjera ? " bg-blue-50" : "")}>Haber ARS</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mayorData.movimientos?.map((mov, i) => {
                const esMe = mov.moneda_id && mov.moneda_id !== 1;
                return (
                  <TableRow key={i} className={esMe ? "bg-blue-50/30" : ""}>
                    <TableCell>{formatDate(mov.fecha)}</TableCell>
                    <TableCell>#{mov.asiento_numero}</TableCell>
                    <TableCell>{mov.concepto}</TableCell>
                    {tieneMonedaExtranjera && <>
                      <TableCell>{esMe ? <Badge variant="outline" className="text-xs">{mov.moneda_codigo}</Badge> : <span className="text-xs text-gray-400">ARS</span>}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{esMe && mov.origen_debe > 0 ? formatCurrency(mov.origen_debe) : ""}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{esMe && mov.origen_haber > 0 ? formatCurrency(mov.origen_haber) : ""}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{esMe ? mov.tipo_cambio?.toFixed(2) : ""}</TableCell>
                    </>}
                    <TableCell className={"text-right font-mono" + (tieneMonedaExtranjera ? " bg-blue-50/50" : "")}>{mov.debe > 0 ? formatCurrency(mov.debe) : ""}</TableCell>
                    <TableCell className={"text-right font-mono" + (tieneMonedaExtranjera ? " bg-blue-50/50" : "")}>{mov.haber > 0 ? formatCurrency(mov.haber) : ""}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatCurrency(mov.saldo)}</TableCell>
                  </TableRow>
                );
              })}
              {(!mayorData.movimientos || mayorData.movimientos.length === 0) && (
                <TableRow><TableCell colSpan={colSpan} className="text-center py-4 text-gray-500">Sin movimientos</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-between p-3 bg-[#2853B9]/5 rounded-lg">
          <span className="font-semibold">Saldo Final:</span>
          <span className="font-bold text-lg">{formatCurrency(mayorData.saldo_final)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reportes-contables">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Reportes Contables</h1>
          <p className="text-gray-600">Informes y analisis del modulo contable</p>
        </div>
        <Select value={ejercicioId} onValueChange={setEjercicioId}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Seleccionar ejercicio" />
          </SelectTrigger>
          <SelectContent>
            {ejercicios.map((ej) => (
              <SelectItem key={ej.id} value={ej.id.toString()}>
                {ej.nombre} {!ej.cerrado && "(Abierto)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border shadow-sm flex-wrap h-auto p-1">
          <TabsTrigger value="mayor" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <BookOpen className="w-4 h-4 mr-2" />Libro Mayor
          </TabsTrigger>
          <TabsTrigger value="diario" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />Libro Diario
          </TabsTrigger>
          <TabsTrigger value="sumas" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4 mr-2" />Balance Sumas y Saldos
          </TabsTrigger>
          <TabsTrigger value="balance" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <DollarSign className="w-4 h-4 mr-2" />Balance General
          </TabsTrigger>
          <TabsTrigger value="resultados" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" />Estado de Resultados
          </TabsTrigger>
        </TabsList>

        {/* LIBRO MAYOR */}
        <TabsContent value="mayor" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <CardTitle className="text-lg">Libro Mayor</CardTitle>
              <CardDescription>Movimientos detallados por cuenta contable</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Modo selector */}
              <div className="flex gap-2 mb-2">
                <Button variant={modoMayor === "individual" ? "default" : "outline"} size="sm"
                  onClick={() => setModoMayor("individual")} className={modoMayor === "individual" ? "bg-[#2853B9]" : ""}>
                  Cuenta individual
                </Button>
                <Button variant={modoMayor === "multiple" ? "default" : "outline"} size="sm"
                  onClick={() => setModoMayor("multiple")} className={modoMayor === "multiple" ? "bg-[#2853B9]" : ""}>
                  Multiples cuentas
                </Button>
              </div>

              {modoMayor === "individual" ? (
                <FiltrosPeriodo fechaDesde={fechaDesde} setFechaDesde={setFechaDesde} fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
                  onConsultar={fetchLibroMayor} loading={loading}>
                  <div className="flex-1 min-w-[250px]">
                    <Label>Cuenta</Label>
                    <CuentaCombobox cuentas={cuentas} value={cuentaSeleccionada} onChange={setCuentaSeleccionada} />
                  </div>
                </FiltrosPeriodo>
              ) : (
                <div className="space-y-3">
                  <FiltrosPeriodo fechaDesde={fechaDesde} setFechaDesde={setFechaDesde} fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
                    onConsultar={fetchMayoresMultiples} loading={loading} />
                  <div className="border rounded-lg p-3 max-h-[200px] overflow-y-auto">
                    <p className="text-sm text-gray-500 mb-2">Seleccione las cuentas ({cuentasSeleccionadas.length} seleccionadas):</p>
                    <div className="space-y-1">
                      {cuentas.map(c => (
                        <div key={c.id} className="flex items-center gap-2 py-1 hover:bg-gray-50 px-2 rounded cursor-pointer"
                          onClick={() => toggleCuentaMultiple(c.id.toString())}>
                          <Checkbox checked={cuentasSeleccionadas.includes(c.id.toString())}
                            onCheckedChange={() => toggleCuentaMultiple(c.id.toString())} />
                          <span className="font-mono text-xs text-gray-500">{c.codigo}</span>
                          <span className="text-sm">{c.nombre || c.descripcion}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Resultados: individual */}
              {libroMayor && modoMayor === "individual" && renderMayorTable(libroMayor)}

              {/* Resultados: multiples */}
              {mayoresMultiples.length > 0 && modoMayor === "multiple" && (
                <div className="space-y-6 mt-4">
                  {mayoresMultiples.map(m => renderMayorTable(m))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LIBRO DIARIO */}
        <TabsContent value="diario" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <CardTitle className="text-lg">Libro Diario</CardTitle>
              <CardDescription>Registro cronologico de asientos contables</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FiltrosPeriodo fechaDesde={fechaDesde} setFechaDesde={setFechaDesde} fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
                onConsultar={fetchLibroDiario} loading={loading} />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Nro.</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead>Concepto</TableHead>
                    <TableHead className="text-right">Debe</TableHead>
                    <TableHead className="text-right">Haber</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {libroDiario.map((asiento, i) => (
                    asiento.detalles?.map((det, j) => (
                      <TableRow key={`${i}-${j}`} className={j === 0 ? "border-t-2" : ""}>
                        {j === 0 && (
                          <>
                            <TableCell rowSpan={asiento.detalles.length} className="align-top font-medium">
                              {formatDate(asiento.fecha)}
                            </TableCell>
                            <TableCell rowSpan={asiento.detalles.length} className="align-top">
                              #{asiento.numero}
                            </TableCell>
                          </>
                        )}
                        <TableCell className="font-mono text-sm">{det.cuenta_codigo}</TableCell>
                        <TableCell>{det.descripcion || asiento.concepto}</TableCell>
                        <TableCell className="text-right font-mono">{det.debe > 0 ? formatCurrency(det.debe) : ""}</TableCell>
                        <TableCell className="text-right font-mono">{det.haber > 0 ? formatCurrency(det.haber) : ""}</TableCell>
                      </TableRow>
                    ))
                  ))}
                  {libroDiario.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Haga clic en Consultar para cargar los asientos</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BALANCE SUMAS Y SALDOS */}
        <TabsContent value="sumas" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <CardTitle className="text-lg">Balance de Sumas y Saldos</CardTitle>
              <CardDescription>Resumen de movimientos y saldos por cuenta</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FiltrosPeriodo fechaDesde={fechaDesde} setFechaDesde={setFechaDesde} fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
                onConsultar={fetchBalanceSumas} loading={loading} />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codigo</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead className="text-right">Sumas Debe</TableHead>
                    <TableHead className="text-right">Sumas Haber</TableHead>
                    <TableHead className="text-right">Saldo Deudor</TableHead>
                    <TableHead className="text-right">Saldo Acreedor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balanceSumas.map((cuenta, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-sm">{cuenta.cuenta_codigo}</TableCell>
                      <TableCell>{cuenta.cuenta_descripcion}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(cuenta.sumas_debe)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(cuenta.sumas_haber)}</TableCell>
                      <TableCell className="text-right font-mono">{cuenta.saldo_deudor > 0 ? formatCurrency(cuenta.saldo_deudor) : ""}</TableCell>
                      <TableCell className="text-right font-mono">{cuenta.saldo_acreedor > 0 ? formatCurrency(cuenta.saldo_acreedor) : ""}</TableCell>
                    </TableRow>
                  ))}
                  {balanceSumas.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Haga clic en Consultar para cargar el balance</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              {balanceSumas.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total Sumas Debe</p>
                    <p className="text-xl font-bold">{formatCurrency(balanceSumas.reduce((s, c) => s + (c.sumas_debe || 0), 0))}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total Sumas Haber</p>
                    <p className="text-xl font-bold">{formatCurrency(balanceSumas.reduce((s, c) => s + (c.sumas_haber || 0), 0))}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BALANCE GENERAL */}
        <TabsContent value="balance" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <div className="flex flex-col gap-2">
                <CardTitle className="text-lg">Balance General</CardTitle>
                <CardDescription>Estado de situacion patrimonial</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <FiltrosPeriodo fechaDesde={fechaDesde} setFechaDesde={setFechaDesde} fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
                onConsultar={fetchBalanceGeneral} loading={loading} />
              {balanceGeneral ? (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-[#1E3876] border-b pb-2">ACTIVO</h3>
                    {balanceGeneral.activo?.map((grupo, i) => (
                      <div key={i} className="space-y-2">
                        <h4 className="font-semibold text-gray-700">{grupo.nombre}</h4>
                        {grupo.cuentas?.map((cuenta, j) => (
                          <div key={j} className="flex justify-between text-sm pl-4">
                            <span>{cuenta.cuenta_descripcion || cuenta.nombre}</span>
                            <span className="font-mono">{formatCurrency(cuenta.saldo)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between font-semibold border-t pt-1">
                          <span>Subtotal {grupo.nombre}</span>
                          <span className="font-mono">{formatCurrency(grupo.subtotal)}</span>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold text-lg bg-[#2853B9]/10 p-3 rounded">
                      <span>TOTAL ACTIVO</span>
                      <span className="font-mono">{formatCurrency(balanceGeneral.total_activo)}</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-[#1E3876] border-b pb-2">PASIVO</h3>
                    {balanceGeneral.pasivo?.map((grupo, i) => (
                      <div key={i} className="space-y-2">
                        <h4 className="font-semibold text-gray-700">{grupo.nombre}</h4>
                        {grupo.cuentas?.map((cuenta, j) => (
                          <div key={j} className="flex justify-between text-sm pl-4">
                            <span>{cuenta.cuenta_descripcion || cuenta.nombre}</span>
                            <span className="font-mono">{formatCurrency(cuenta.saldo)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between font-semibold border-t pt-1">
                          <span>Subtotal {grupo.nombre}</span>
                          <span className="font-mono">{formatCurrency(grupo.subtotal)}</span>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold bg-gray-100 p-2 rounded">
                      <span>TOTAL PASIVO</span>
                      <span className="font-mono">{formatCurrency(balanceGeneral.total_pasivo)}</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#1E3876] border-b pb-2 mt-6">PATRIMONIO NETO</h3>
                    {balanceGeneral.patrimonio?.map((grupo, i) => (
                      <div key={i} className="space-y-2">
                        {grupo.cuentas?.map((cuenta, j) => (
                          <div key={j} className="flex justify-between text-sm pl-4">
                            <span>{cuenta.cuenta_descripcion || cuenta.nombre}</span>
                            <span className="font-mono">{formatCurrency(cuenta.saldo)}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                    <div className="flex justify-between font-bold bg-gray-100 p-2 rounded">
                      <span>TOTAL PATRIMONIO NETO</span>
                      <span className="font-mono">{formatCurrency(balanceGeneral.total_patrimonio)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg bg-[#2853B9]/10 p-3 rounded">
                      <span>TOTAL PASIVO + PN</span>
                      <span className="font-mono">{formatCurrency(balanceGeneral.total_pasivo + balanceGeneral.total_patrimonio)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">Seleccione periodo y haga clic en Consultar</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ESTADO DE RESULTADOS */}
        <TabsContent value="resultados" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <CardTitle className="text-lg">Estado de Resultados</CardTitle>
              <CardDescription>Ingresos, egresos y resultado del ejercicio</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FiltrosPeriodo fechaDesde={fechaDesde} setFechaDesde={setFechaDesde} fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
                onConsultar={fetchEstadoResultados} loading={loading} />
              {estadoResultados ? (
                <div className="max-w-2xl mx-auto space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-bold text-lg text-green-700 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />INGRESOS
                    </h3>
                    {estadoResultados.ingresos?.map((ing, i) => (
                      <div key={i} className="flex justify-between text-sm pl-4">
                        <span>{ing.nombre}</span>
                        <span className="font-mono text-green-600">{formatCurrency(ing.monto)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Total Ingresos</span>
                      <span className="font-mono text-green-700">{formatCurrency(estadoResultados.total_ingresos)}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-bold text-lg text-red-700 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5" />EGRESOS
                    </h3>
                    {estadoResultados.egresos?.map((egr, i) => (
                      <div key={i} className="flex justify-between text-sm pl-4">
                        <span>{egr.nombre}</span>
                        <span className="font-mono text-red-600">{formatCurrency(egr.monto)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Total Egresos</span>
                      <span className="font-mono text-red-700">{formatCurrency(estadoResultados.total_egresos)}</span>
                    </div>
                  </div>
                  <div className={`p-6 rounded-lg ${estadoResultados.resultado >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">
                        {estadoResultados.resultado >= 0 ? 'GANANCIA DEL EJERCICIO' : 'PERDIDA DEL EJERCICIO'}
                      </span>
                      <span className={`text-2xl font-bold font-mono ${estadoResultados.resultado >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(Math.abs(estadoResultados.resultado))}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">Seleccione periodo y haga clic en Consultar</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportesContables;
